import React from 'react';
import { useSelector } from 'react-redux';
import { update } from '../../services/item';
import { deepCloneArray } from '../../utils/array';
import { canEditItemForItem, getId } from '../../utils/item';
import { ModalGlobal } from '../modal/modal.Global';
import { ConditionBig } from './condition.Big';
import { ConditionLittle } from './condition.Little';
import { ConditionRound } from './condition.Round';
import { ConditionCheckbox } from './condition.Checkbox';
import { ConditionSettings } from './condition.Settings';
import { TooltipHtml } from '../tooltip/tooltip.Html';
import { TextMd } from '../text/text.Md';
import { useBoardReadOnlyMode } from '../../context/boardReadOnly';
import { isEditable } from '../../utils/roles';

export const ConditionGlobal = props => {
  const { itemId, conditionId, condition, fieldInGrid, canEdit: canEditProp } =
    props;
  const inheritedBoardReadOnlyMode = useBoardReadOnlyMode();

  const item = useSelector(state => state.items[itemId]);
  const items = useSelector(state => state.items);
  const isMobile = useSelector(state => state.global.isMobile);
  const canEdit =
    inheritedBoardReadOnlyMode === 'hard'
      ? false
      : canEditProp ?? canEditItemForItem(item, items);
  const canInteract =
    isEditable() && !inheritedBoardReadOnlyMode && canEdit;

  const updateField = (value, field) => {
    if (!canEdit) {
      return;
    }
    const clonedConditions = deepCloneArray(item.conditions || []);
    let idx = -1;
    for (let i = 0; i < clonedConditions.length; i++) {
      const conditionEntry = clonedConditions[i];
      if (getId(conditionEntry) === conditionId) {
        idx = i;
        break;
      }
    }
    if (idx === -1) return;

    const currentCondition = clonedConditions[idx][conditionId];

    const setInactiveGroup = groupName => {
      if (!groupName) return;
      for (let i = 0; i < clonedConditions.length; i++) {
        const otherId = getId(clonedConditions[i]);
        if (otherId === conditionId) continue;
        const otherCondition = clonedConditions[i][otherId];
        if (otherCondition?.group === groupName) {
          otherCondition.state = 'inactive';
        }
      }
    };

    const ensureGroupHasActive = groupName => {
      if (!groupName) return;
      const hasActive = clonedConditions.some(conditionEntry => {
        const id = getId(conditionEntry);
        if (id === conditionId) {
          return clonedConditions[idx][conditionId].state === 'active';
        }
        const entry = conditionEntry[id];
        return entry?.group === groupName && entry?.state === 'active';
      });
      if (!hasActive) {
        // try to pick a default
        const defaultCandidate = clonedConditions.find(conditionEntry => {
          const id = getId(conditionEntry);
          if (id === conditionId) return false;
          const entry = conditionEntry[id];
          return entry?.group === groupName && entry?.defaultInGroup;
        });
        if (defaultCandidate) {
          const id = getId(defaultCandidate);
          defaultCandidate[id].state = 'active';
          setInactiveGroup(groupName);
          defaultCandidate[id].state = 'active';
        }
      }
    };

    if (field === 'state') {
      if (value === 'active') {
        setInactiveGroup(currentCondition?.group);
      } else if (value === 'inactive') {
        clonedConditions[idx][conditionId][field] = value;
        ensureGroupHasActive(currentCondition?.group);
        update(`items/${itemId}`, 'conditions', clonedConditions);
        return;
      }
    }

    if (field === 'group') {
      const newGroup = value;
      clonedConditions[idx][conditionId][field] = value;
      if (currentCondition?.state === 'active') {
        setInactiveGroup(newGroup);
        ensureGroupHasActive(newGroup);
      }
      update(`items/${itemId}`, 'conditions', clonedConditions);
      return;
    }

    if (field === 'defaultInGroup') {
      clonedConditions[idx][conditionId][field] = value;
      ensureGroupHasActive(currentCondition?.group);
      update(`items/${itemId}`, 'conditions', clonedConditions);
      return;
    }

    clonedConditions[idx][conditionId][field] = value;
    update(`items/${itemId}`, 'conditions', clonedConditions);
  };

  const renderCondition = () => {
    if (
      fieldInGrid?.displayType === 'big' ||
      fieldInGrid?.displayType === 'conditions'
    ) {
      return (
        <ConditionBig
          updateField={updateField}
          condition={condition}
          canInteract={canInteract}
        />
      );
    }
    if (fieldInGrid?.displayType === 'little') {
      return (
        <ConditionLittle
          updateField={updateField}
          condition={condition}
          canInteract={canInteract}
        />
      );
    }
    if (fieldInGrid?.displayType === 'rond') {
      return (
        <ConditionRound
          updateField={updateField}
          condition={condition}
          fieldInGrid={fieldInGrid}
          canInteract={canInteract}
        />
      );
    }
    if (fieldInGrid?.displayType === 'checkbox') {
      return (
        <ConditionCheckbox
          updateField={updateField}
          condition={condition}
          canInteract={canInteract}
        />
      );
    }
    return null;
  };

  const tooltipContent =
    typeof condition?.tooltip === 'string' && condition.tooltip.trim() !== ''
      ? condition.tooltip.trim()
      : null;

  const conditionContent = renderCondition();
  const isBigDisplay =
    fieldInGrid?.displayType === 'big' ||
    fieldInGrid?.displayType === 'conditions';
  const wrappedConditionContent = (
    <div className={`w-full box-border ${isBigDisplay ? 'px-1' : ''}`}>
      {conditionContent}
    </div>
  );
  const displayContent =
    tooltipContent && conditionContent && !isMobile ? (
      <TooltipHtml
        title={
          <div className='max-w-xs break-words text-sm leading-normal'>
            <TextMd
              str={tooltipContent}
              textId={`${conditionId}-tooltip`}
              itemId={itemId}
            />
          </div>
        }
        enterDelay={1000}
        enterNextDelay={1000}
      >
        {wrappedConditionContent}
      </TooltipHtml>
    ) : (
      wrappedConditionContent
    );

  return (
    <div className='h-full w-full flex items-center justify-center'>
      {canEdit && (
        <ModalGlobal
          title={'Configuration'}
          idToOpen={conditionId}
        >
          <ConditionSettings
            itemId={itemId}
            conditionId={conditionId}
            condition={condition}
            updateField={updateField}
          />
        </ModalGlobal>
      )}
      {displayContent}
    </div>
  );
};
